/**
 * Renderizado del terminal CRT.
 *
 * No usamos un framework reactivo: la función `renderTerminal` regenera
 * el HTML completo del `<div id="terminal">` cada vez que cambia el
 * estado. El terminal tiene <100 líneas de log en el peor caso, así
 * que el coste es despreciable.
 *
 * Estructura HTML pintada:
 *   <div class="header-line">...</div>
 *   <pre class="dos">
 *     ...boot lines fijas...
 *     ...líneas del log con nivel...
 *     [opciones clicables si view === 'menu']
 *     [prompt activo + buffer + cursor]
 *   </pre>
 */

import { dosify } from './font-substitutions.js';
import { promptForView } from './flows.js';
import { listProfiles } from './monitor-profiles.js';
import type { AppState, LogLevel, ViewId } from './state.js';

interface RenderRefs {
  /** Contenedor `<div class="terminal">`. */
  terminal: HTMLElement;
}

const BOOT_LINES = [
  { text: 'The 51SQN Personal Computer DOS', level: 'normal' as const },
  { text: 'Versión 1.99 (C)Copyright 51SQN  2012', level: 'normal' as const },
  { text: '', level: 'normal' as const },
];

const MENU_GUEST: Array<{ key: string; label: string }> = [
  { key: '1', label: 'LOGIN' },
  { key: '2', label: 'REGISTRO' },
  { key: '3', label: 'RECUPERAR PASSWORD' },
  { key: '4', label: 'CONFIG' },
  { key: '5', label: 'ACERCA DE' },
  { key: '6', label: 'SALIR' },
];

const MENU_AUTH: Array<{ key: string; label: string }> = [
  { key: '1', label: 'LOGOUT' },
  { key: '2', label: 'REGISTRO' },
  { key: '3', label: 'RECUPERAR PASSWORD' },
  { key: '4', label: 'CONFIG' },
  { key: '5', label: 'ACERCA DE' },
  { key: '6', label: 'SALIR' },
];

const CONFIG_MENU: Array<{ key: string; label: string }> = [
  { key: '1', label: 'PERFIL DE PANTALLA' },
  { key: '2', label: 'MODO DÍA/TARDE/NOCHE' },
  { key: '3', label: 'VOLVER' },
];

/** Opciones del sub-menú ACERCA DE (vista `about`). */
const ABOUT_MENU: Array<{ key: string; label: string }> = [
  { key: '1', label: 'AVISO LEGAL' },
  { key: '2', label: 'POLÍTICA DE PRIVACIDAD' },
  { key: '3', label: 'TÉRMINOS DE USO' },
  { key: '4', label: 'POLÍTICA DE COOKIES' },
  { key: '5', label: 'VOLVER' },
];

const TIME_OF_DAY_FIXED: Array<{
  key: string;
  value: 'auto' | 'day' | 'dusk' | 'night';
  label: string;
  era: string;
}> = [
  { key: '1', value: 'auto',  label: 'AUTO',  era: 'Día(6h/19h), Tarde(19h/21:30h), Noche(21:30/6h)' },
  { key: '2', value: 'day',   label: 'DÍA',   era: 'forzar versión diurna' },
  { key: '3', value: 'dusk',  label: 'TARDE', era: 'forzar versión atardecer' },
  { key: '4', value: 'night', label: 'NOCHE', era: 'forzar versión nocturna' },
];

const CYCLE_PERIODS: Array<{ secs: 10 | 30 | 60; tab: string }> = [
  { secs: 10, tab: '10s' },
  { secs: 30, tab: '30s' },
  { secs: 60, tab: '1MIN' },
];

export function renderTerminal(state: AppState, refs: RenderRefs): void {
  // Preservamos el host del widget Turnstile (si existe) para que el
  // re-render no lo elimine — el iframe de Cloudflare es caro de
  // recrear y perdería el token ya resuelto.
  const turnstileHost = refs.terminal.querySelector<HTMLElement>('#turnstile-host');

  const html = `
    ${renderHeader(state)}
    <pre class="dos" id="dos">${renderBody(state)}</pre>
  `;
  refs.terminal.innerHTML = html;

  if (turnstileHost) {
    refs.terminal.appendChild(turnstileHost);
  }

  // Auto-scroll: al final del log para que el prompt esté visible. La
  // vista `about_doc` es la excepción — un documento legal largo se lee
  // desde arriba, así que la posicionamos al inicio.
  const dos = refs.terminal.querySelector<HTMLElement>('#dos');
  if (dos) {
    dos.scrollTop = state.view === 'about_doc' ? 0 : dos.scrollHeight;
  }
}

/**
 * Actualiza el datetime del header sin re-pintar el log entero. Llamar
 * cada segundo desde un `setInterval`.
 */
export function tickDatetime(refs: RenderRefs): void {
  const el = refs.terminal.querySelector<HTMLElement>('#datetime');
  if (el) el.textContent = formatDateTime(new Date());
}

/* ─── Header BIOS ──────────────────────────────────────────────────────── */

function renderHeader(state: AppState): string {
  const userText = state.authenticatedUser
    ? ' ' + state.authenticatedUser.callsign
    : '';
  return `<div class="header-line">
    <span>${escapeHtml(dosify('51 Escuadrón' + userText))}</span>
    <span id="datetime">${formatDateTime(new Date())}</span>
  </div>`;
}

function formatDateTime(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy}  ${hh}:${mi}:${ss}`;
}

/* ─── Cuerpo del terminal ─────────────────────────────────────────────── */

function renderBody(state: AppState): string {
  const lines: string[] = [];

  // Boot fijo arriba.
  for (const line of BOOT_LINES) {
    lines.push(formatLine(dosify(line.text), line.level));
  }

  // Log acumulado.
  for (const entry of state.log) {
    lines.push(formatLine(dosify(entry.text), entry.level));
  }

  // Vista actual.
  if (state.view === 'menu') {
    lines.push(...renderMenu(state));
    lines.push('');
    lines.push(renderPrompt(state, 'A>'));
  } else if (state.view === 'config') {
    lines.push(...renderOptions(CONFIG_MENU));
    lines.push('');
    lines.push(renderPrompt(state, 'A>'));
  } else if (state.view === 'config_screen_profile') {
    lines.push(...renderProfileMenu(state));
    lines.push('');
    lines.push(renderPrompt(state, 'A>'));
  } else if (state.view === 'config_time_of_day') {
    lines.push(...renderTimeOfDayMenu(state));
    lines.push('');
    lines.push(renderPrompt(state, 'A>'));
  } else if (state.view === 'about') {
    lines.push(...renderOptions(ABOUT_MENU));
    lines.push('');
    lines.push(renderPrompt(state, 'A>'));
  } else if (isInputView(state.view)) {
    const prompt = promptForView(state.view);
    lines.push(renderPrompt(state, prompt));
  } else if (isBusyView(state.view)) {
    lines.push(`<span class="exec-info">${dosify(promptForView(state.view))}</span>`);
  }

  return lines.join('\n');
}

function renderMenu(state: AppState): string[] {
  const options = state.authenticatedUser ? MENU_AUTH : MENU_GUEST;
  return renderOptions(options);
}

function renderOptions(options: ReadonlyArray<{ key: string; label: string }>): string[] {
  return options.map(
    (m) =>
      `  <span class="opt" data-key="${m.key}">${m.key}. ${escapeHtml(dosify(m.label))}</span>`,
  );
}

/**
 * Listado del selector de perfiles. Cada línea muestra:
 *   1. (X) MONOCROMO VERDE       IBM 5151 fósforo P1
 * El (X) marca el perfil actualmente activo en `state.currentProfile`.
 */
function renderProfileMenu(state: AppState): string[] {
  const profiles = listProfiles();
  const LABEL_WIDTH = 20;
  return profiles.map((p, i) => {
    const key = String(i + 1);
    const marker = p.id === state.currentProfile ? '(X)' : '( )';
    const label = p.label.padEnd(LABEL_WIDTH);
    const text = `${key}. ${marker} ${label}${p.era}`;
    return `  <span class="opt" data-key="${key}">${escapeHtml(dosify(text))}</span>`;
  });
}

/**
 * Listado del selector MODO DÍA/TARDE/NOCHE. Mismo patrón visual que
 * `renderProfileMenu` — (X) marca la opción activa según
 * `state.currentTimeOfDay`.
 *
 * La entrada CICLO (clave 5) es especial: el (X) marca si cualquier
 * `cycle-*` está activo, y al pulsarla rota el período (10s → 30s →
 * 1min → 10s). Debajo se renderiza una sub-fila tabulada con los tres
 * períodos para mostrar visualmente el seleccionado.
 */
function renderTimeOfDayMenu(state: AppState): string[] {
  const LABEL_WIDTH = 12;
  const cyclePeriod = parseCyclePeriod(state.currentTimeOfDay);
  const isCycle = cyclePeriod !== null;
  const displayPeriod = cyclePeriod ?? 10;

  const lines = TIME_OF_DAY_FIXED.map((o) => {
    const marker = o.value === state.currentTimeOfDay ? '(X)' : '( )';
    const label = o.label.padEnd(LABEL_WIDTH);
    const text = `${o.key}. ${marker} ${label}${o.era}`;
    return `  <span class="opt" data-key="${o.key}">${escapeHtml(dosify(text))}</span>`;
  });

  // Entrada CICLO con período actual entre paréntesis.
  const cycleMarker = isCycle ? '(X)' : '( )';
  const cycleLabel = `CICLO (${formatCyclePeriod(displayPeriod)})`.padEnd(LABEL_WIDTH);
  const cycleText = `5. ${cycleMarker} ${cycleLabel}rotar día/tarde/noche`;
  lines.push(`  <span class="opt" data-key="5">${escapeHtml(dosify(cycleText))}</span>`);

  // Sub-fila decorativa: tabs de períodos. El (X) sólo aparece si CICLO
  // está activo; si no, las tres salen "( )" mientras 10s queda como
  // valor por defecto para cuando el usuario active el modo.
  const tabsText = CYCLE_PERIODS.map(
    (t) => `${isCycle && t.secs === displayPeriod ? '(X)' : '( )'} ${t.tab}`,
  ).join('   ');
  lines.push(`       ${escapeHtml(dosify(tabsText))}`);

  return lines;
}

function parseCyclePeriod(pref: AppState['currentTimeOfDay']): 10 | 30 | 60 | null {
  if (pref === 'cycle-10') return 10;
  if (pref === 'cycle-30') return 30;
  if (pref === 'cycle-60') return 60;
  return null;
}

function formatCyclePeriod(secs: number): string {
  return secs === 60 ? '1min' : `${secs}s`;
}

function renderPrompt(state: AppState, prompt: string): string {
  const promptText = escapeHtml(dosify(prompt));
  const buffer = state.inputIsPassword
    ? '*'.repeat(state.inputBuffer.length)
    : escapeHtml(dosify(state.inputBuffer));
  const cursor = state.inputDisabled
    ? ''
    : '<span class="cursor"></span>';
  return `${promptText} ${buffer}${cursor}`;
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function isInputView(v: ViewId): boolean {
  return (
    v === 'login_callsign' ||
    v === 'login_password' ||
    v === 'register_callsign' ||
    v === 'register_email' ||
    v === 'register_password' ||
    v === 'register_password_confirm' ||
    v === 'register_turnstile' ||
    v === 'verify_email_resend' ||
    v === 'recovery_request' ||
    v === 'recovery_set_password'
  );
}

function isBusyView(v: ViewId): boolean {
  return (
    v === 'login_busy' ||
    v === 'register_busy' ||
    v === 'verify_email_checking'
  );
}

function formatLine(text: string, level: LogLevel): string {
  const safe = escapeHtml(text);
  switch (level) {
    case 'msg':   return `<span class="exec-msg">${safe}</span>`;
    case 'warn':  return `<span class="exec-warn">${safe}</span>`;
    case 'info':  return `<span class="exec-info">${safe}</span>`;
    case 'error': return `<span class="exec-error">${safe}</span>`;
    case 'title': return `<span class="section-title">${safe}</span>`;
    case 'normal': return safe;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
